"use client";

import { Button } from "@/components/ui/button";
import Image from "next/image";
import Link from "next/link";
import { useEffect } from "react";
import { useTranslations } from "next-intl";
import { recoverFromChunkError } from "@/lib/chunk-error";

const ERROR_ENDPOINT = "/api/error-reporting";

// Function to report error to the server
const reportError = async (error: Error & { digest?: string }) => {
  try {
    const errorReport = {
      type: "client-error",
      message: error.message,
      stack: error.stack,
      digest: error.digest,
      timestamp: new Date().toISOString(),
      context: {
        userAgent: typeof window !== "undefined" ? navigator.userAgent : "",
        url: typeof window !== "undefined" ? window.location.href : "",
        timestamp: Date.now(),
      },
    };

    await fetch(ERROR_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(errorReport),
    });
  } catch (reportingError) {
    // If error reporting fails, log to console as fallback
    console.error("Failed to report error to server:", reportingError);
  }
};

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const t = useTranslations("Errors");

  useEffect(() => {
    // Log the error to an error reporting service
    console.error(error);

    // Errores de carga de chunk (deploy/rollout): recargar una vez en vez de
    // mostrar la pantalla de error. Si recarga, no seguimos con el reporte.
    if (recoverFromChunkError(error)) return;

    // Report error to server
    reportError(error);
  }, [error]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4">
      <div className="flex max-w-md flex-col items-center space-y-8 text-center">
        <div className="flex flex-col items-center space-y-4">
          <Image
            src="/logo-full.svg"
            alt="RAJO TRANSPORTES"
            width={200}
            height={200}
            priority
            className="mb-6"
            style={{ height: "auto" }}
          />
          <h1 className="text-4xl font-bold text-destructive">
            {t("errorOccurred")}
          </h1>
          <h2 className="text-2xl font-semibold text-foreground">
            {t("errorTitle")}
          </h2>
        </div>

        <p className="text-muted-foreground">{t("errorDescription")}</p>
        <p>{error?.message ?? ""}</p>

        <div className="flex w-full flex-col gap-4 sm:w-auto sm:flex-row">
          <Button
            onClick={reset}
            variant="default"
            size="lg"
            className="w-full sm:w-auto"
          >
            {t("tryAgain")}
          </Button>
          <Button
            asChild
            variant="outline"
            size="lg"
            className="w-full sm:w-auto"
          >
            <Link href="/">{t("backToHome")}</Link>
          </Button>
        </div>

        {error.digest && (
          <p className="text-xs text-muted-foreground">
            Código de error: {error.digest}
          </p>
        )}
      </div>
    </div>
  );
}
